<template>
  <b-section :class="`bg-${backgroundColor}`">
    <div class="category-explorer">
      <div
        class="category-explorer__container container"
        id="category-selector">
        <slot name="header" :total-categories="totalCategories">
          <b-headline
              type="h2"
              typography="h2"
              :class="{
            'category-explorer__headline mb-4 m:mb-6': true,
            'text-center': centered
          }">
            {{ $t("headline") }}
          </b-headline>

          <b-subheadline
            :class="{
              'category-explorer__subheadline mb-8 m:mb-12': true,
              'text-center': centered
            }"
            v-html="subheadline" />
        </slot>

        <category-selector
          :categories="categories"
          :active-category="activeCategory"
          :chip-color="chipColor"
          :modifiers="alignmentModifiers"
          :collapsed-categories-on-mobile="collapsedCategoriesOnMobile"
          @categoryChange="selectCategory"
          class="category-explorer__category-selector" />

        <category-book-slider
          ref="fixedCategory"
          v-if="activeCategory"
          :key="activeCategory.title"
          :category="activeCategory"
          :are-books-linked="areBooksLinked"
          :clickable-books="clickableBooks"
          class="category-explorer__category-book-slider">
          <template v-slot:header="categoryHeaderProps">
            <slot name="category-header"
                  :total-books="categoryHeaderProps.totalBooks"
                  :title="categoryHeaderProps.title">
            </slot>
          </template>
        </category-book-slider>

        <template v-if="showBooksFromOtherCategories">
          <line-divider spacing="l" />

          <category-book-slider
              v-for="category in categories.slice(1, 3)"
              :key="`fixed-${category.title}`"
              :category="category"
              :are-books-linked="areBooksLinked"
              :clickable-books="clickableBooks"
              class="category-explorer__category-book-slider" />
        </template>

        <a
          v-if="showScrollUp"
          class="category-explorer__view-categories"
          @click="scrollToElement('#category-selector')">{{ $t('scrollToAllCategories') }} </a>

        <start-trial-button-with-pricing
          v-if="showStartTrial"
          :show-trial-end-date="showTrialEndDate"
          :cta-class="ctaClass"
          :cta-copy="ctaCopy" />
      </div>
    </div>
  </b-section>
</template>

<script>
import BSection from "@/shared/section";
import StartTrialButtonWithPricing from "@/shared/start-trial-button-with-pricing";
import BHeadline from "@/component-library/b-headline";
import BSubheadline from "@/component-library/b-subheadline";
import CategorySelector from "@/component-library/category-selector";
import CategoryBookSlider from "@/component-library/category-book-slider";
import LineDivider from "@/component-library/line-divider";

import globals from "@/utils/globals";
import smoothscroll from "smoothscroll-polyfill";

smoothscroll.polyfill();

export default {
  name: "CategoryExplorer",
  components: {
    BSection,
    BHeadline,
    BSubheadline,
    CategorySelector,
    CategoryBookSlider,
    StartTrialButtonWithPricing,
    LineDivider
  },
  props: {
    categories: {
      type: Array,
      required: true
    },
    defaultCategorySlug: {
      type: String,
      required: false,
      default: ""
    },
    showTrialEndDate: {
      type: Boolean,
      default: false
    },
    showScrollUp: {
      type: Boolean,
      default: true
    },
    showStartTrial: {
      type: Boolean,
      default: true
    },
    showBooksFromOtherCategories: {
      type: Boolean,
      default: true
    },
    clickableBooks: {
      type: Boolean,
      default: true
    },
    backgroundColor: {
      type: String,
      default: "pale-mint"
    },
    ctaClass: {
      type: String,
      required: false,
      default: null
    },
    ctaCopy: {
      type: String,
      required: false,
      default: null
    },
    areBooksLinked: {
      type: Boolean,
      default: true
    },
    centered: {
      type: Boolean,
      default: true
    },
    collapsedCategoriesOnMobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeCategory: null
    };
  },
  computed: {
    subheadline() {
      return this.$t("subheadline", { totalBooks: globals.totalBooks });
    },
    chipColor() {
      return this.backgroundColor === "pale-mint"
        ? "bg-white"
        : "bg-pale-mint-grey";
    },
    alignmentModifiers() {
      if (!this.centered) {
        return ["left-aligned"];
      } else {
        return [];
      }
    },
    totalCategories() {
      return this.categories.length;
    }
  },
  mounted() {
    let category;

    if (this.defaultCategorySlug) {
      category = this.categories.find((cat) => cat.slug === this.defaultCategorySlug);
    }

    this.activeCategory = category ? category : this.categories[0];
  },
  methods: {
    selectCategory(category) {
      this.activeCategory = category;

      this.scrollToFixedCategoryOnMobile();
    },
    scrollToFixedCategoryOnMobile() {
      if (!window.matchMedia("(min-width: 767px)").matches) {
        const headerHeightPlusPadding = 86;
        const top = this.$refs.fixedCategory.$el.getBoundingClientRect().top - headerHeightPlusPadding;

        window.scrollBy({
          top,
          left: 0,
          behavior: "smooth"
        });
      }
    },
    scrollToElement(query) {
      const el = this.$el.querySelector(query);
      el.scrollIntoView({
        behavior: "smooth"
      });
    }
  },
  i18n: {
    messages: {
      en: {
        scrollToAllCategories: "Scroll up to all categories",
        headline: "What are you interested in?",
        subheadline: "With over {totalBooks} nonfiction books, we have the <mark><strong>most comprehensive</strong></mark> library of its kind."
      },
      de: {
        scrollToAllCategories: "Nach oben für alle Kategorien",
        headline: "Was interessiert dich besonders?",
        subheadline: "Mit mehr als als {totalBooks} Titeln erhältst du bei Blinkist <mark><strong>branchenweit eines der größten Angebote</strong></mark>."
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.category-explorer {

  &__category-selector {
    margin-bottom: $spacing-48;

    @include media(min-width, m) {
      margin-bottom: $spacing-64;
    }
  }

  &__category-book-slider {

    &:not(:last-of-type) {
      margin-bottom: $spacing-32;
      @include media(min-width, m) {
        margin-bottom: $spacing-48;
      }
    }

    &:last-of-type {
      margin-bottom: $spacing-32;
    }
  }

  &__view-categories {
    display: block;
    margin-bottom: $spacing-48;

    text-align: center;

    text-decoration: underline;

    cursor: pointer;
  }
}

.bg-white {
  background-color: $white;
}

.bg-pale-mint {
  background-color: $pale-mint-grey;
}
</style>
